<template>
  <v-dialog v-model="internalValue" max-width="500px" scrollable>
    <v-card class="rounded-xl border border-white-05 bg-surface-variant">
      <v-card-title class="d-flex align-center pa-4 border-b border-white-05">
        <span class="text-h6 font-weight-black">{{ title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" size="small" @click="internalValue = false"></v-btn>
      </v-card-title>

      <v-card-text class="pa-0" style="height: 500px;">
        <div v-if="loading && !users.length" class="d-flex align-center justify-center h-100">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <v-list v-else-if="users.length > 0" lines="two" bg-color="transparent">
          <template v-for="(item, index) in users" :key="item.did">
            <v-list-item @click="navigateToProfile(item.handle)">
              <template v-slot:prepend>
                <v-avatar size="48" class="mr-2">
                  <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                  <v-icon v-else icon="mdi-account"></v-icon>
                </v-avatar>
              </template>

              <v-list-item-title class="font-weight-bold">
                {{ item.displayName || item.handle }}
              </v-list-item-title>
              <v-list-item-subtitle>
                @{{ item.handle }}
              </v-list-item-subtitle>

              <template v-slot:append v-if="!isCurrentUser(item.did)">
                <v-btn 
                  v-if="!item.viewer?.following"
                  size="small" 
                  color="primary" 
                  rounded="pill" 
                  class="text-none px-4"
                  @click.stop="handleFollow(item)"
                  :loading="followingDids.has(item.did)"
                >
                  Follow
                </v-btn>
                <v-btn 
                  v-else
                  size="small" 
                  variant="outlined" 
                  rounded="pill" 
                  class="text-none px-4"
                  @click.stop="handleUnfollow(item)"
                  :loading="followingDids.has(item.did)"
                >
                  Following
                </v-btn>
              </template>
            </v-list-item>
            <v-divider v-if="index < users.length - 1" class="border-white-05 mx-4"></v-divider>
          </template>

          <div v-if="cursor" class="pa-4 text-center">
            <v-btn 
              variant="text" 
              color="primary" 
              class="text-none" 
              :loading="loading" 
              @click="loadMore"
            >
              Load More
            </v-btn>
          </div>
        </v-list>

        <div v-else class="d-flex flex-column align-center justify-center h-100 text-grey">
          <v-icon icon="mdi-account-group-outline" size="x-large" class="mb-2"></v-icon>
          <span>No users found</span>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getLikes, getRepostedBy, followUser, unfollowUser, currentUser, getFollowers, getFollows } from '@/services/bluesky';

const props = defineProps<{
  modelValue: boolean;
  type: 'likes' | 'reposts' | 'quotes' | 'followers' | 'follows';
  postUri?: string;
  actor?: string;
}>();

const emit = defineEmits(['update:modelValue']);

const router = useRouter();
const users = ref<any[]>([]);
const loading = ref(false);
const cursor = ref<string | undefined>(undefined);
const followingDids = ref<Set<string>>(new Set());

const internalValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const title = computed(() => {
  switch (props.type) {
    case 'likes': return 'Liked by';
    case 'reposts': return 'Reposted by';
    case 'quotes': return 'Quoted by';
    case 'followers': return 'Followers';
    case 'follows': return 'Following';
    default: return 'Users';
  }
});

const isCurrentUser = (did: string) => currentUser.value?.did === did;

const fetchData = async (isLoadMore = false) => {
  loading.value = true;
  
  try {
    let result: any;
    if (props.type === 'likes' && props.postUri) {
      result = await getLikes(props.postUri, 20, cursor.value);
      const newUsers = result.likes.map((l: any) => l.actor);
      if (isLoadMore) users.value = [...users.value, ...newUsers];
      else users.value = newUsers;
    } else if (props.type === 'reposts' && props.postUri) {
      result = await getRepostedBy(props.postUri, 20, cursor.value);
      if (isLoadMore) users.value = [...users.value, ...result.repostedBy];
      else users.value = result.repostedBy;
    } else if (props.type === 'followers' && props.actor) {
      result = await getFollowers(props.actor, 20, cursor.value);
      if (isLoadMore) users.value = [...users.value, ...result.followers];
      else users.value = result.followers;
    } else if (props.type === 'follows' && props.actor) {
      result = await getFollows(props.actor, 20, cursor.value);
      if (isLoadMore) users.value = [...users.value, ...result.follows];
      else users.value = result.follows;
    }
    
    cursor.value = result?.cursor;
  } catch (error) {
    console.error('Failed to fetch users:', error);
  } finally {
    loading.value = false;
  }
};

const loadMore = () => fetchData(true);

const handleFollow = async (user: any) => {
  followingDids.value.add(user.did);
  try {
    const res = await followUser(user.did);
    user.viewer = user.viewer || {};
    user.viewer.following = res.uri;
  } catch (e) {
    console.error('Follow failed:', e);
  } finally {
    followingDids.value.delete(user.did);
  }
};

const handleUnfollow = async (user: any) => {
  if (!user.viewer?.following) return;
  followingDids.value.add(user.did);
  try {
    await unfollowUser(user.viewer.following);
    user.viewer.following = undefined;
  } catch (e) {
    console.error('Unfollow failed:', e);
  } finally {
    followingDids.value.delete(user.did);
  }
};

const navigateToProfile = (handle: string) => {
  internalValue.value = false;
  router.push(`/profile/${handle}`);
};

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    users.value = [];
    cursor.value = undefined;
    fetchData();
  }
});
</script>

<style scoped>
.bg-surface-variant {
  background-color: rgba(11, 18, 25, 0.76) !important;
  backdrop-filter: blur(40px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(40px) saturate(180%) !important;
  color: white !important;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
:deep(.v-list-item-title) {
  color: white !important;
}
:deep(.v-list-item-subtitle) {
  color: rgba(255, 255, 255, 0.7) !important;
}
:deep(.v-card-title) {
  color: white !important;
}
</style>
